import { useEffect, useMemo, useRef, useState } from "react";
import {
    transactionExtendRows,
    transactionSetRowReturnDate,
    type TransactionDiscRow,
} from "../../api/specialized.ts";
import { useToast } from "../../hooks/useToast.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import styles from "./transactionOpsModal.module.scss";

interface Props {
    transactionId: number;
    /** Rental rows + kiosk offset come from the parent's bundle fetch — the
     *  card itself never hits the server until the admin clicks Extend or
     *  Mark-as-returned. Parent calls onChanged() after a successful
     *  operation to trigger a fresh bundle pull. */
    rows: TransactionDiscRow[];
    kioskOffset: number;
    /** Permission gate — when false the whole card is hidden. */
    canWrite: boolean;
    /** Tell the parent to refetch the bundle after a successful write. */
    onChanged: () => void;
}

/** Replaces WebAdmin2's "Return-date Operations" popup. Lives as one card on
 *  the Actions tab — no nested modal, no second round-trip to open. Pattern:
 *  tick the rows to act on, run one of the two operations. The server stamps
 *  an audit note on success (see TransactionSetRowReturnDate). */
export function RentalReturnsCard({ transactionId, rows, kioskOffset, canWrite, onChanged }: Props) {
    const { toast } = useToast();
    const { confirm } = useAlertDialog();
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [extendDays, setExtendDays] = useState(1);
    const [returnDate, setReturnDate] = useState("");
    const [busy, setBusy] = useState<null | "extend" | "return">(null);
    const seededDate = useRef(false);

    // Seed the return-date picker once when rows first arrive — earliest
    // DueByDate so the admin's first click is rarely far off.
    useEffect(() => {
        if (!seededDate.current && rows.length > 0 && rows[0]!.DueByDate) {
            setReturnDate(utcToKioskLocalInput(rows[0]!.DueByDate, kioskOffset));
            seededDate.current = true;
        }
    }, [rows, kioskOffset]);

    const toggle = (rowId: number) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(rowId) ? next.delete(rowId) : next.add(rowId);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === rows.length) setSelected(new Set());
        else setSelected(new Set(rows.map(r => r.TransactionRowID)));
    };

    const selectedIds = useMemo(() => Array.from(selected), [selected]);
    const canAct = canWrite && selectedIds.length > 0 && !busy;

    const runExtend = async () => {
        if (!canAct || extendDays <= 0) return;
        setBusy("extend");
        try {
            const res = await transactionExtendRows(transactionId, selectedIds, extendDays);
            if (res.bError || res.oData !== true) {
                toast(res.sMessage ?? "Failed to extend due date", "error");
            } else {
                toast(`Due date extended by ${extendDays} day${extendDays === 1 ? "" : "s"} for ${selectedIds.length} row${selectedIds.length === 1 ? "" : "s"}`, "success");
                onChanged();
            }
        } finally {
            setBusy(null);
        }
    };

    const runReturn = async () => {
        if (!canAct || !returnDate) return;
        // Picker value is kiosk-local wall-clock ("YYYY-MM-DDTHH:mm"). Storage
        // convention is UTC, so convert back using the kiosk's current offset
        // before sending. Some module SPs (NeoCaddie) override the hour to
        // 14:00 kiosk-local at insert time — admin can't see that from here,
        // but it's well-documented in Web_SetTransactionItemReturnDateNeoCaddie_SP.
        const isoUtc = kioskLocalInputToUtcIso(returnDate, kioskOffset);
        if (!isoUtc) {
            toast("Could not parse the picked return date", "error");
            return;
        }
        const label = `${returnDate.replace("T", " ")} (${formatOffset(kioskOffset)} kiosk local)`;
        const ok = await confirm({
            title: `Mark ${selectedIds.length} row${selectedIds.length === 1 ? "" : "s"} as returned?`,
            body: (
                <>
                    <p style={{ margin: 0 }}>Return date: <b>{label}</b></p>
                    <p style={{ margin: "var(--space-2) 0 0", color: "var(--color-text-muted)" }}>
                        This only updates transaction processing — product inventory state is not changed.
                    </p>
                </>
            ),
            confirmLabel: "Mark as returned",
            destructive: true,
        });
        if (!ok) return;
        setBusy("return");
        try {
            const res = await transactionSetRowReturnDate(transactionId, selectedIds, isoUtc);
            if (res.bError || res.oData !== true) {
                toast(res.sMessage ?? "Failed to set return date", "error");
            } else {
                toast(`Return date set for ${selectedIds.length} row${selectedIds.length === 1 ? "" : "s"}`, "success");
                setSelected(new Set());
                onChanged();
            }
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className={styles.actionCard}>
            <h4 className={styles.cardTitle}>
                Rental returns
                {rows.length > 0 && (
                    <span className={styles.muted} style={{ marginLeft: "var(--space-2)", fontWeight: 400 }}>
                        · times shown in kiosk local ({formatOffset(kioskOffset)})
                    </span>
                )}
            </h4>

            {rows.length === 0 && (
                <p className={styles.muted}>No rental rows on this transaction.</p>
            )}

            {rows.length > 0 && (<>
                <table className={styles.paymentTable}>
                    <thead>
                        <tr>
                            <th style={{ width: 32 }}>
                                <input
                                    type="checkbox"
                                    checked={selected.size === rows.length}
                                    onChange={toggleAll}
                                    aria-label="Select all rows"
                                />
                            </th>
                            <th>Product</th>
                            <th>Due</th>
                            <th>Delivered</th>
                            <th>Returned</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => {
                            const checked = selected.has(r.TransactionRowID);
                            const alreadyReturned = !!r.ReturnDate;
                            return (
                                <tr key={r.TransactionRowID} className={alreadyReturned ? styles.muted : undefined}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(r.TransactionRowID)}
                                            aria-label={`Select ${r.Product}`}
                                        />
                                    </td>
                                    <td>{r.Product}</td>
                                    <td>{fmtKioskLocal(r.DueByDate, kioskOffset)}</td>
                                    <td>{fmtKioskLocal(r.DeliverDate, kioskOffset)}</td>
                                    <td>{fmtKioskLocal(r.ReturnDate, kioskOffset)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div className={styles.actionRow} style={{ marginTop: "var(--space-3)" }}>
                    <label className={styles.muted} style={{ minWidth: 130 }}>Extend due by</label>
                    <input
                        className={styles.textInput}
                        type="number"
                        min={1}
                        max={365}
                        value={extendDays}
                        onChange={e => setExtendDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                        style={{ width: 90 }}
                    />
                    <span className={styles.muted}>day{extendDays === 1 ? "" : "s"}</span>
                    <button
                        className={styles.actionBtn}
                        onClick={runExtend}
                        disabled={!canAct || extendDays <= 0 || busy === "extend"}
                        title={!canWrite ? "Missing permission: execTransactionReturnOps"
                             : selectedIds.length === 0 ? "Tick at least one row above"
                             : ""}
                    >
                        {busy === "extend" ? "Extending…" : "Extend due date"}
                    </button>
                </div>

                <div className={styles.actionRow}>
                    <label className={styles.muted} style={{ minWidth: 130 }}>
                        Set return date<br />
                        <span style={{ fontSize: "var(--text-xs)" }}>(kiosk local)</span>
                    </label>
                    <input
                        className={styles.textInput}
                        type="datetime-local"
                        value={returnDate}
                        onChange={e => setReturnDate(e.target.value)}
                        style={{ width: 220 }}
                    />
                    <button
                        className={styles.refundBtn}
                        onClick={runReturn}
                        disabled={!canAct || !returnDate || busy === "return"}
                        title={!canWrite ? "Missing permission: execTransactionReturnOps"
                             : selectedIds.length === 0 ? "Tick at least one row above"
                             : !returnDate ? "Pick a return date"
                             : ""}
                    >
                        {busy === "return" ? "Saving…" : "Mark as returned"}
                    </button>
                </div>

                {!canWrite && (
                    <p className={styles.muted} style={{ marginTop: "var(--space-2)" }}>
                        Read-only — write permission on <code>execTransactionReturnOps</code> required to run operations.
                    </p>
                )}
            </>)}
        </div>
    );
}

// ── helpers ────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, "0");

/** Take a server-issued UTC ISO string and render it in the kiosk's local
 *  wall clock — "YYYY-MM-DD HH:mm". Uses the offset from
 *  WebGetKioskCurrentOffsetMinutes; if it's 0 the result equals UTC, which
 *  matches the server's fallback when no Cache_TimezoneLookup row resolves. */
function fmtKioskLocal(iso: string | null, offsetMinutes: number): string {
    if (!iso) return "—";
    try {
        const utc = new Date(iso);
        if (isNaN(utc.getTime())) return iso;
        const local = new Date(utc.getTime() + offsetMinutes * 60000);
        return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`;
    } catch { return iso; }
}

/** UTC ISO → "YYYY-MM-DDTHH:mm" in the kiosk's TZ, for seeding the
 *  `<input type="datetime-local">` picker. */
function utcToKioskLocalInput(iso: string, offsetMinutes: number): string {
    try {
        const utc = new Date(iso);
        if (isNaN(utc.getTime())) return "";
        const local = new Date(utc.getTime() + offsetMinutes * 60000);
        return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}T${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`;
    } catch { return ""; }
}

/** Picker value (kiosk-local wall clock, no TZ suffix) → UTC ISO with Z.
 *  Inverse of utcToKioskLocalInput — subtract the kiosk offset to recover
 *  the UTC instant. Returns null on parse failure. */
function kioskLocalInputToUtcIso(localStr: string, offsetMinutes: number): string | null {
    // localStr is "YYYY-MM-DDTHH:mm" — parse as UTC parts (no Date.parse
    // ambiguity with browser TZ), then subtract kiosk offset.
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localStr);
    if (!m) return null;
    const [y, mo, d, h, mi, s] = [m[1]!, m[2]!, m[3]!, m[4]!, m[5]!, m[6] ?? "00"].map(Number);
    const asUtc = Date.UTC(y!, mo! - 1, d!, h!, mi!, s!);
    if (isNaN(asUtc)) return null;
    return new Date(asUtc - offsetMinutes * 60000).toISOString();
}

/** "+02:00" / "-05:30" / "UTC" — picker label suffix. */
function formatOffset(offsetMinutes: number): string {
    if (offsetMinutes === 0) return "UTC";
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    return `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
